'use client';

import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import * as HeroIconsSolid from '@heroicons/react/24/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

type IconVariant = 'outline' | 'solid';

interface IconProps {
    name: string;
    variant?: IconVariant;
    size?: number;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    [key: string]: any;
}

/**
 * Maps Lucide-style icon names (used throughout the app) to their
 * correct Heroicons v2 PascalCase equivalents.
 */
const LUCIDE_TO_HEROICONS: Record<string, string> = {
  // Common renames
  X: 'XMark',
  Info: 'InformationCircle',
  Loader2: 'ArrowPath',
  Upload: 'ArrowUpTray',
  Search: 'MagnifyingGlass',
  Pencil: 'PencilSquare',
  Trash2: 'Trash',
  Globe: 'GlobeAlt',
  ChevronsUpDown: 'ChevronUpDown',
  Save: 'ArrowDownTray',
  ScanSearch: 'MagnifyingGlassCircle',
  GitMerge: 'ArrowsPointingIn',
  KeyRound: 'Key',
  UserPlus: 'UserPlus',
  UserCircle: 'UserCircle',
  User: 'User',
  Film: 'Film',
  Check: 'Check',
  CheckCircle: 'CheckCircle',
  ExclamationTriangle: 'ExclamationTriangle',
  Star: 'Star',
  ChevronUp: 'ChevronUp',
  ChevronDown: 'ChevronDown',
  ChevronLeft: 'ChevronLeft',
  ChevronRight: 'ChevronRight',
};

/** Icons that have no Heroicons equivalent — rendered as inline SVG */
const CUSTOM_SVG_ICONS: Record<string, React.FC<{ size: number; className?: string }>> = {
  Instagram: ({ size, className }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  Twitter: ({ size, className }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
};

function Icon({
    name,
    variant = 'outline',
    size = 24,
    className = '',
    onClick,
    disabled = false,
    ...props
}: IconProps) {
    // Check for custom SVG icons first (no Heroicons equivalent)
    const CustomSvg = CUSTOM_SVG_ICONS[name];
    if (CustomSvg) {
        return (
            <CustomSvg
                size={size}
                className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
            />
        );
    }

    // Resolve Lucide alias → Heroicons name
    const resolvedName = LUCIDE_TO_HEROICONS[name] ?? name;
    const iconKey = `${resolvedName}Icon` as keyof typeof HeroIcons;

    const iconSet = variant === 'solid' ? HeroIconsSolid : HeroIcons;
    const IconComponent = iconSet[iconKey] as React.ComponentType<any>;

    if (!IconComponent) {
        return (
            <QuestionMarkCircleIcon
                width={size}
                height={size}
                className={`text-gray-400 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                onClick={disabled ? undefined : onClick}
                {...props}
            />
        );
    }

    return (
        <IconComponent
            width={size}
            height={size}
            className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
            onClick={disabled ? undefined : onClick}
            {...props}
        />
    );
}

export default Icon;